import { Text, Box, Hide } from 'pcln-design-system'
import PropTypes from 'prop-types'
import React from 'react'
import DisplayPrice from '../DisplayPrice/DisplayPrice'
import {
  AlignedText,
  FareBrandName,
  LargerCentered,
  ShowBoxLarger,
  Wrapper,
  MobileOnlyAffirmBox,
} from './FareBrandBox.styles'
import IconRow from './IconRow'

const FareBrandBox = ({
  ancillaries,
  fareBrandName,
  crossOutText,
  memberSignIn,
  price,
  seatsAvailable,
  subPriceTexts,
  priceColor,
  affirmNode,
  isReshopExperience,
  reshopPerPersonText,
  creditAppliedStamp,
  isUpsellTray,
  isNewItineraryDesign,
  isMobile,
  ...props
}) => {
  const hasFareBrandName = fareBrandName !== ''

  return (
    <Wrapper
      flexDirection='column'
      pt={[2, 3]}
      pb={[2, isUpsellTray ? 2 : 3]}
      pr={[2, 0]}
      alignItems={['flex-end', 'center']}
      justifyContent='center'
      data-testid='farebrand-box-wrapper'
      width={['25%', '28%']}
      {...props}
    >
      {isReshopExperience && (
        <Hide xs sm mb='12px'>
          {creditAppliedStamp}
        </Hide>
      )}

      {memberSignIn || hasFareBrandName || crossOutText !== '' ? (
        <Box width={1}>
          {memberSignIn && <ShowBoxLarger pb={2}>{memberSignIn}</ShowBoxLarger>}

          {hasFareBrandName ? (
            <AlignedText pb={isReshopExperience ? [2, 1] : 2} fontSize={'10px'} bold>
              <FareBrandName
                pr={[0, 1]}
                pl={1}
                data-testid='fare-brand-name'
                fontSize={isReshopExperience && '10px'}
              >
                {fareBrandName}
              </FareBrandName>
            </AlignedText>
          ) : null}

          {crossOutText && (
            <AlignedText color='text.light' fontSize={0}>
              <Text.s>{crossOutText}</Text.s>
            </AlignedText>
          )}
        </Box>
      ) : null}

      <Box mt={['auto', 0]} mb={['auto', isReshopExperience ? 'auto' : 0]}>
        <LargerCentered pt={hasFareBrandName ? 2 : 0}>
          <DisplayPrice
            prefaceText={price.text}
            dollar={price.dollar}
            cents={price.cents}
            color={priceColor}
            isResponsive
          />
        </LargerCentered>
        {isReshopExperience && reshopPerPersonText ? (
          <AlignedText color='text.light' fontSize={'10px'} whiteSpace='pre'>
            {reshopPerPersonText}
          </AlignedText>
        ) : (
          <>
            {subPriceTexts.map((text) => (
              <AlignedText
                mt={isNewItineraryDesign && !isMobile && 1}
                color='text.light'
                fontSize={'10px'}
                key={text}
                whiteSpace='nowrap'
              >
                {text}
              </AlignedText>
            ))}

            {isUpsellTray && !isNewItineraryDesign && (
              <Hide xs sm md lg mt={1}>
                {affirmNode}
              </Hide>
            )}
          </>
        )}
        {ancillaries.length > 0 && (
          <IconRow ancillaries={ancillaries} mt={isNewItineraryDesign && !isMobile && 0} />
        )}

        {!isReshopExperience && seatsAvailable > 0 && (
          <AlignedText mt={1} fontSize={'10px'} color='warning.base' whiteSpace='nowrap'>
            <Hide xs sm md>
              <Text.span bold>
                {seatsAvailable} seat{seatsAvailable > 1 && 's'}
              </Text.span>
              <Text.span>&nbsp;left at this price</Text.span>
            </Hide>
            <Hide lg xl xxl>
              <Text bold>
                {seatsAvailable} seat{seatsAvailable > 1 && 's'} left
              </Text>
              <Text>&nbsp;at this price</Text>
            </Hide>
          </AlignedText>
        )}
      </Box>

      {affirmNode && (
        <MobileOnlyAffirmBox pt={2} shouldHide={isReshopExperience}>
          {affirmNode}
        </MobileOnlyAffirmBox>
      )}

      {affirmNode && !isReshopExperience && isNewItineraryDesign && !isMobile && (
        <Box mt={2}>{affirmNode}</Box>
      )}
    </Wrapper>
  )
}

FareBrandBox.defaultProps = {
  ancillaries: [],
  price: {},
  subPriceTexts: [],
}

FareBrandBox.propTypes = {
  /** List of ancillaries */
  ancillaries: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      description: PropTypes.string,
      inclusion: PropTypes.string,
      size: PropTypes.number,
      desktopSize: PropTypes.number,
    })
  ),

  /** Fare brand name string */
  fareBrandName: PropTypes.string,

  /** texts rendering below the price */
  subPriceTexts: PropTypes.arrayOf(PropTypes.string),

  /** Crossout price text */
  crossOutText: PropTypes.string,

  /** Member sign in styled node */
  memberSignIn: PropTypes.node,

  /** Pricing info */
  price: PropTypes.shape({
    dollar: PropTypes.string,
    cents: PropTypes.string,
    text: PropTypes.string,
  }),

  /** Price color */
  priceColor: PropTypes.string,

  /** Number of seats available */
  seatsAvailable: PropTypes.number,

  affirmNode: PropTypes.node,

  /** UI changes for rebook/reshop experience */
  isReshopExperience: PropTypes.bool,

  /** Per person copy for rebook/reshop experience */
  reshopPerPersonText: PropTypes.string,

  /** Credit Applied stamp for Reshop/Rebook */
  creditAppliedStamp: PropTypes.element,

  /** Bottom tray with upsell options on listings */
  isUpsellTray: PropTypes.bool,

  /** AIR_LISTINGS_CARD_REFACTOR_DESKTOP */
  isNewItineraryDesign: PropTypes.bool,

  isMobile: PropTypes.bool,
}

export default FareBrandBox
